import React, { useContext, useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import {
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
  Box,
} from "@mui/material"
import { createFilterOptions } from "@mui/material/Autocomplete"
import { useSnackbar } from "notistack"
import AddIcon from "@mui/icons-material/Add"
import { EditOutlined } from "@mui/icons-material"
import i18n from "i18n"
import { useQueryClient } from "@tanstack/react-query"
import AuthContext from "context/auth/authContext"
import {
  useContactsLight,
  useCompanies,
  useContactTags,
  useCreateContact,
  useEditContact,
  useContact,
} from "../../Contacts/hooks/useContacts"
import ContactModal from "../ContactModal"

const contactNameLabel = option =>
  `${option.firstname || ""} ${option.lastname || ""}`.trim()

export default function ContactField({
  value,
  onChange,
  label,
  size = "medium",
  readOnly = false,
  required = false,
  error = false,
  onInvalidContact,
}) {
  const { user } = useContext(AuthContext)
  const { enqueueSnackbar } = useSnackbar()
  const organizationId = user["custom:organizationId"]
  const queryClient = useQueryClient()

  const [modalOpen, setModalOpen] = useState(false)
  const [contactToEdit, setContactToEdit] = useState(null)
  const [pendingEditId, setPendingEditId] = useState(null)

  const createContactMutation = useCreateContact()
  const editContactMutation = useEditContact()

  const { data = [], isLoading } = useContactsLight(
    organizationId,
    !!organizationId,
  )

  const fullContactQuery = useContact(
    organizationId,
    pendingEditId,
    !!pendingEditId && !!organizationId,
  )

  const { data: companies = [] } = useCompanies(
    organizationId,
    modalOpen && !!organizationId,
  )
  const { data: contactTags = [] } = useContactTags(
    organizationId,
    modalOpen && !!organizationId,
  )

  const selectedObj = data.find(c => c.SK === value) || null

  useEffect(() => {
    if (
      !isLoading &&
      data.length > 0 &&
      value &&
      !selectedObj &&
      typeof onInvalidContact === "function"
    ) {
      onInvalidContact()
    }
  }, [value, selectedObj, onInvalidContact, isLoading, data.length])

  useEffect(() => {
    if (!pendingEditId) return
    if (fullContactQuery.data) {
      setContactToEdit(fullContactQuery.data)
      setModalOpen(true)
      setPendingEditId(null)
    } else if (fullContactQuery.isError) {
      enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyLoaded"), {
        variant: "error",
      })
      setPendingEditId(null)
    }
  }, [
    pendingEditId,
    fullContactQuery.data,
    fullContactQuery.isError,
    enqueueSnackbar,
  ])

  const handleAddNewContact = () => {
    setContactToEdit(null)
    setModalOpen(true)
  }

  const handleEditContact = () => {
    if (selectedObj) {
      setPendingEditId(selectedObj.SK)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setContactToEdit(null)
  }

  const handleSaveContact = (contactData, companyId) => {
    const isEditing = contactToEdit && contactToEdit.PK && contactToEdit.SK

    if (isEditing) {
      editContactMutation.mutate(
        { contactData, companyId },
        {
          onSuccess: updatedContact => {
            setModalOpen(false)
            setContactToEdit(null)

            queryClient.setQueryData(
              ["contactsLight", organizationId],
              (oldData = []) => {
                return oldData.map(c =>
                  c.SK === updatedContact.SK ? updatedContact : c,
                )
              },
            )

            queryClient.invalidateQueries({ queryKey: ["contacts"] })
            if (updatedContact && updatedContact.SK) {
              queryClient.invalidateQueries({
                queryKey: ["contact", organizationId, updatedContact.SK],
              })
            }

            if (updatedContact && updatedContact.SK) {
              onChange(null, updatedContact)
            }
          },
          onError: err => {
            enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyUpdated"), {
              variant: "error",
            })
            console.error("Error editing contact:", err)
          },
        },
      )
    } else {
      createContactMutation.mutate(
        { organizationId, contactData, companyId },
        {
          onSuccess: newContact => {
            setModalOpen(false)

            queryClient.setQueryData(
              ["contactsLight", organizationId],
              (oldData = []) => {
                if (oldData.some(c => c.SK === newContact.SK)) {
                  return oldData.map(c =>
                    c.SK === newContact.SK ? newContact : c,
                  )
                }
                return [...oldData, newContact]
              },
            )

            queryClient.invalidateQueries({ queryKey: ["contacts"] })

            if (newContact && newContact.SK) {
              onChange(null, newContact)
            }
          },
          onError: err => {
            enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyCreated"), {
              variant: "error",
            })
            console.error("Error creating contact:", err)
          },
        },
      )
    }
  }

  const showError =
    error || (!isLoading && data.length > 0 && value && !selectedObj)

  const filterOptions = useMemo(
    () =>
      createFilterOptions({
        stringify: contactNameLabel,
        ignoreCase: true,
        ignoreAccents: true,
        trim: true,
      }),
    [],
  )

  return (
    <>
      <Box
        sx={{
          position: "relative",
          "&:hover .add-contact-icon": { opacity: 1 },
        }}
      >
        <Autocomplete
          options={data}
          getOptionLabel={contactNameLabel}
          getOptionKey={option => option.SK}
          filterOptions={filterOptions}
          isOptionEqualToValue={(option, value) => option.SK === value.SK}
          value={selectedObj}
          onChange={
            readOnly ? undefined : (_e, newValue) => onChange(_e, newValue)
          }
          loading={isLoading}
          open={readOnly ? false : undefined}
          disableClearable={readOnly ? true : undefined}
          readOnly={readOnly}
          slotProps={{
            textField: {
              inputProps: {
                tabIndex: readOnly ? -1 : 0,
                style: readOnly ? { pointerEvents: "none" } : {},
              },
            },
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label || i18n.t("contacts.chooseContact")}
              variant="outlined"
              margin="normal"
              size={size}
              fullWidth
              required={required}
              error={showError}
              sx={{ m: 0, ...(readOnly ? { background: "#f5f5f5" } : {}) }}
              InputProps={{
                ...params.InputProps,
                readOnly,
                endAdornment: readOnly ? (
                  isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null
                ) : (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {!readOnly && (
          <>
            {selectedObj ? (
              <IconButton
                onClick={handleEditContact}
                title={i18n.t("contacts.edit")}
                aria-label={i18n.t("contacts.edit")}
                className="add-contact-icon"
                size="small"
                disabled={
                  !!pendingEditId &&
                  (fullContactQuery.isLoading || fullContactQuery.isFetching)
                }
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 62,
                  transform: "translateY(-50%)",
                  color: "#515151",
                  opacity:
                    !!pendingEditId &&
                    (fullContactQuery.isLoading || fullContactQuery.isFetching)
                      ? 1
                      : 0,
                  transition: "opacity 0.2s",
                }}
              >
                {!!pendingEditId &&
                (fullContactQuery.isLoading || fullContactQuery.isFetching) ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <EditOutlined fontSize="small" />
                )}
              </IconButton>
            ) : (
              <IconButton
                onClick={handleAddNewContact}
                title={i18n.t("contacts.addNewContact")}
                aria-label={i18n.t("contacts.addNewContact")}
                className="add-contact-icon"
                size="small"
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 32,
                  transform: "translateY(-50%)",
                  color: "#515151",
                  opacity: 0,
                  transition: "opacity 0.2s",
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </>
        )}
      </Box>
      <ContactModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveContact}
        contact={contactToEdit}
        companies={companies}
        contactTags={contactTags}
        organizationId={organizationId}
        queryClient={queryClient}
      />
    </>
  )
}

ContactField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  onInvalidContact: PropTypes.func,
}
